import Icon from '@amzn/awsui-components-core/icon';
import Button from '@amzn/awsui-components-core/button';
import Dropdown from '@amzn/awsui-components-core/dropdown';
import { FilterDropdownHeader } from 'src/components/star-grid/components/FilterDropdownHeader';
import Input from '@amzn/awsui-components-core/input';
import { SelectProps } from '@amzn/awsui-components-core/select';
import StatusIndicator from '@amzn/awsui-components-core/status-indicator';
import TreeView from '@amzn/awsui-components-core/tree-view';
import React from 'react';
import { VersionPickerIcon } from 'src/components/star-grid/components/VersionPickerIcon';
import { ReportVersionDateActions } from 'src/components/star-grid/components/ReportVersionDateActions';
import { ReportVersionDateFilter } from 'src/components/star-grid/components/ReportVersionDateFilter';
import { VERSION_DROPDOWN_WIDTH } from 'src/components/star-grid/versionDateFilterConstants';
import { versionCountText } from 'src/components/star-grid/utils/versionPickerPresentation';
import { useVersionDropdown } from 'src/components/star-grid/hooks/useVersionDropdown';
import { VersionChoices, VersionTreeItem } from 'src/components/star-grid/reportSelectors.types';
import { ReportFilterField } from 'src/components/star-grid/components/ReportFilterField';
import styles from 'src/components/star-grid/components/styles/ReportVersionSelector.module.scss';

interface ReportVersionSelectorProps {
  readonly label: string;
  readonly controlId: string;
  readonly control: SelectProps;
  readonly choices: VersionChoices;
}
type VersionDropdownState = ReturnType<typeof useVersionDropdown>;

const VersionChoice: React.FC<{
  readonly item: VersionTreeItem;
  readonly state: VersionDropdownState;
  readonly kind: 'live' | 'tagged' | 'snapshot';
}> = ({ item, state, kind }) => (
  <div className={styles.choice}>
    <Button
      variant="inline-link"
      formAction="none"
      disabled={item.version?.resolvable === false}
      onClick={() => state.select(item)}
      data-testid={`version-choice-${item.version?.version}`}
      ariaLabel={item.label}
      nativeButtonAttributes={{ 'aria-pressed': item.version?.version === state.selected, 'data-kind': kind }}
    >
      <span className={styles.choiceContent}>
        <span className={styles.choiceIcon} aria-hidden="true">
          <VersionPickerIcon kind={kind} />
        </span>
        <span className={styles.choiceCopy}>
          <span className={styles.choiceLabel}>{item.displayLabel ?? item.label}</span>
          {kind === 'live' && <span className={styles.choiceDescription}>{state.text('versionLiveDescription')}</span>}
        </span>
        {kind !== 'live' && item.description && (
          <time className={styles.choiceTime} dateTime={item.dateTime}>
            {item.description}
          </time>
        )}
        {item.version?.version === state.selected && <Icon name="check" size="small" />}
      </span>
    </Button>
    {item.version?.resolvable === false && <span className={styles.unavailable}>{state.text('versionHistoryUnavailable')}</span>}
  </div>
);

const VersionHistory: React.FC<{ readonly state: VersionDropdownState }> = ({ state }) => (
  <TreeView<VersionTreeItem>
    items={state.catalog.snapshotGroups}
    expandedItems={state.expanded}
    onItemToggle={state.toggleItem}
    ariaLabel={state.text('reportVersion')}
    getItemId={(item) => item.id}
    getItemChildren={(item) => item.children}
    i18nStrings={{
      expandButtonLabel: () => state.text('expandVersionGroup'),
      collapseButtonLabel: () => state.text('collapseVersionGroup')
    }}
    renderItem={(item) => ({
      content: item.version ? <VersionChoice item={item} state={state} kind="snapshot" /> : <span className={styles.groupLabel}>{item.label}</span>,
      announcementLabel: item.label
    })}
  />
);

const TaggedVersions: React.FC<{ readonly state: VersionDropdownState }> = ({ state }) => (
  <section className={styles.tagged} aria-label={state.text('versionTaggedSnapshots')}>
    <div className={styles.sectionHeading}>
      <span>
        {state.text('versionTaggedSnapshots')} <span className={styles.tagCount}>{state.catalog.tagged.length}</span>
      </span>
    </div>
    {state.preview.tags.map((item) => (
      <VersionChoice key={item.id} item={item} state={state} kind="tagged" />
    ))}
    {state.preview.remainingTags > 0 && (
      <div className={styles.showMore}>
        <Button
          variant="inline-link"
          formAction="none"
          onClick={state.preview.toggleTags}
          data-testid="version-show-tags"
          nativeButtonAttributes={{ 'aria-expanded': state.preview.showTags }}
        >
          {state.preview.showTags ? state.text('versionShowLess') : versionCountText(state.text('versionShowMore'), state.preview.remainingTags)}
        </Button>
      </div>
    )}
  </section>
);

const SnapshotResults: React.FC<{ readonly state: VersionDropdownState; readonly finished: boolean }> = ({ state, finished }) => (
  <>
    <div className={styles.snapshotResults}>
      {state.preview.showSnapshots ? (
        <VersionHistory state={state} />
      ) : (
        state.preview.snapshots.map((item) => <VersionChoice key={item.id} item={item} state={state} kind="snapshot" />)
      )}
      {finished && state.catalog.snapshots.length === 0 && (
        <div className={styles.emptySnapshots}>
          <strong>{state.text('versionNoSnapshots')}</strong>
          <span>{state.text('versionTryDates')}</span>
        </div>
      )}
    </div>
    {state.catalog.snapshots.length > 0 && (
      <div className={styles.showMore}>
        {state.preview.remainingSnapshots > 0 && (
          <Button
            variant="inline-link"
            onClick={state.preview.toggleSnapshots}
            data-testid="version-show-snapshots"
            nativeButtonAttributes={{ 'aria-expanded': state.preview.showSnapshots }}
          >
            {state.preview.showSnapshots
              ? state.text('versionShowLess')
              : versionCountText(state.text('versionShowMore'), state.preview.remainingSnapshots)}
          </Button>
        )}
      </div>
    )}
  </>
);

const VersionSections: React.FC<{ readonly state: VersionDropdownState; readonly control: SelectProps }> = ({ state, control }) => (
  <>
    {state.catalog.tagged.length > 0 && <TaggedVersions state={state} />}
    <section className={styles.snapshots} aria-label={state.text('versionUntaggedSnapshots')}>
      <div className={styles.sectionHeading}>
        <span>{state.text('versionUntaggedSnapshots')}</span>
        <span className={styles.count} aria-live="polite">
          {versionCountText(state.text('versionSnapshotCount'), state.catalog.snapshots.length, state.catalog.totalSnapshots)}
        </span>
      </div>
      <ReportVersionDateFilter state={state} />
      {!state.dates.editing && <SnapshotResults state={state} finished={control.statusType === 'finished'} />}
    </section>
  </>
);

const VersionStatus: React.FC<{ readonly control: SelectProps; readonly choices: VersionChoices; readonly state: VersionDropdownState }> = ({
  control,
  choices,
  state
}) => (
  <>
    {control.statusType === 'loading' && <StatusIndicator type="loading">{control.loadingText}</StatusIndicator>}
    {control.statusType === 'error' && (
      <div role="alert">
        <StatusIndicator type="error">{control.errorText}</StatusIndicator>
        <Button variant="inline-link" onClick={choices.retry}>
          {control.recoveryText}
        </Button>
      </div>
    )}
    {choices.historyIncomplete && control.statusType === 'finished' && (
      <div className={styles.catalogNotice} role="status">
        <StatusIndicator type="warning">{state.text('incompleteVersionHistory')}</StatusIndicator>
        <Button variant="inline-link" onClick={choices.retry}>
          {control.recoveryText}
        </Button>
      </div>
    )}
    {control.statusType === 'finished' && !state.catalog.hasMatches && (
      <div role="status">{state.query.trim() ? control.noMatch : control.empty}</div>
    )}
  </>
);

export const ReportVersionSelector: React.FC<ReportVersionSelectorProps> = ({ label, controlId, control, choices }) => {
  const state = useVersionDropdown(control, choices);
  return (
    <ReportFilterField label={label} controlId={controlId} value={control.selectedOption?.label ?? control.placeholder}>
      <Dropdown
        open={state.isOpen}
        onOutsideClick={state.close}
        onEscape={state.escape}
        onFocusLeave={state.close}
        expandToViewport
        minWidth={320}
        maxWidth={VERSION_DROPDOWN_WIDTH}
        ariaRole="dialog"
        ariaLabel={label}
        header={<FilterDropdownHeader controlId={controlId} />}
        trigger={
          <Button
            ref={state.triggerRef}
            disabled={control.disabled}
            onClick={state.toggle}
            ariaLabel={`${label}: ${control.selectedOption?.label ?? control.placeholder}`}
            ariaHaspopup="dialog"
            ariaExpanded={state.isOpen}
            nativeButtonAttributes={{ id: controlId }}
            data-testid={controlId}
          >
            {control.selectedOption?.label ?? control.placeholder}
          </Button>
        }
        content={
          <div id="stargrid-version-picker" className={styles.picker}>
            <div className={styles.header}>
              <div className={styles.pickerTitle}>
                <h2>{state.text('versionPickerTitle')}</h2>
                <Button variant="icon" iconName="close" formAction="none" onClick={state.dismiss} ariaLabel={state.text('versionClosePicker')} />
              </div>
              <div className={styles.search}>
                <Input
                  type="search"
                  value={state.query}
                  onChange={({ detail }) => state.setQuery(detail.value)}
                  autoFocus
                  placeholder={state.text('searchVersions')}
                  ariaLabel={state.text('searchVersions')}
                  clearAriaLabel={state.text('clearVersionSearch')}
                />
              </div>
              {state.catalog.live && <VersionChoice item={state.catalog.live} state={state} kind="live" />}
            </div>
            <div className={styles.history}>
              <VersionSections state={state} control={control} />
              <VersionStatus control={control} choices={choices} state={state} />
            </div>
            {state.dates.editing && <ReportVersionDateActions state={state} />}
            <div className={styles.footer}>
              <span title={control.selectedOption?.label}>
                {state.text('versionSelected').replace('{version}', control.selectedOption?.label ?? '')}
              </span>
              <span>{state.text('versionNewestFirst')}</span>
            </div>
          </div>
        }
      />
    </ReportFilterField>
  );
};
